import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import SubmitYourMusicPage from './SubmitYourMusicPage';
import SubmitYourMusicGuidedPreviewPage from './SubmitYourMusicGuidedPreviewPage';
import ThankYouPage from './ThankYouPage';
import PlaylistsPage from './PlaylistsPage';
import SupportPage from './SupportPage';
import SupportThankYouPage from './SupportThankYouPage';
import myWayMyStyleGif from './MY_WAY_-_MY_STYLE.gif'; // Импортируем GIF
import './osci-logo-engine-v26-transparent';
import { DecodedArtImage, ResponsiveDecodedBackground } from './components/DecodedVisuals';
import {
  LOGO_SEQUENCE,
  OSCI_SVG_ASSET_URLS,
  getInitialLogoVisualIndexForPage,
  getNextGlobalLogoVisualIndex,
} from './logoVisuals';
import { applyRandomFlicker, decodeTextLocked, runAfterFontsReady } from './utils/textEffects';
import { getPlaylistFollowerDisplayCount } from './data/playlistFollowers';
import { handleSubmitMusicIntentClick, initMetaPixel } from './metaPixel';
import { trackPageView } from './siteAnalytics';
import { useRegionalVisibility } from './regionalVisibility';
import {
  PUBLIC_SUBMISSION_REL,
  PUBLIC_SUBMISSION_TARGET,
  PUBLIC_SUBMISSION_URL,
  SUBMITHUB_DIRECT_TEMPORARY_MODE,
  SUBMITHUB_DIRECT_PREVIEW_PATH,
  SUBMITHUB_DIRECT_URL,
} from './paymentMaintenance';

const SITE_URL = 'https://ivagonzo.com';
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/static/img/logo_email.jpg`;
const SUBMITHUB_PROFILE_URL = 'https://www.submithub.com/playlister/iva-gonzo';
const PUBLIC_ROUTE_META = {
  '/': {
    title: 'Iva Gonzo | Spotify Playlist Curator, Music Review & Underground Playlists',
    description:
      'Iva Gonzo is an independent Spotify playlist curator and Dark Trap / Phonk artist offering music review, written feedback, and possible playlist consideration for underground artists.',
    canonicalPath: '/',
  },
  '/submit-your-music': {
    title: 'Spotify Playlist Submission | Iva Gonzo Curator Review & Feedback',
    description:
      'Submit music to independent Spotify playlist curator Iva Gonzo for review, written feedback, and possible playlist consideration. No paid placement or guaranteed streams.',
    canonicalPath: '/submit-your-music',
  },
  '/playlists': {
    title: 'Iva Gonzo Spotify Playlists | Dark Trap, Phonk & Underground Curator',
    description:
      'Explore Iva Gonzo Spotify playlists for dark trap, phonk, trap metal, hyperpop, shoegaze, post-punk, electronic, dnb, reggaeton, afrobeat, and underground sounds.',
    canonicalPath: '/playlists',
  },
  '/support': {
    title: 'Support Iva Gonzo | Independent Playlists & Music Curation',
    description:
      "Support Iva Gonzo's independent playlist curation, music discovery, artist spotlighting, and dark alternative creative work.",
    canonicalPath: '/support',
  },
};
const NOINDEX_ROUTE_META = {
  '/thank-you': {
    title: 'Thank You | Iva Gonzo',
    description: 'Post-purchase submission page for Iva Gonzo clients.',
    canonicalPath: '/submit-your-music',
  },
  '/support/thank-you': {
    title: 'Thank You | Iva Gonzo Support',
    description: 'Support confirmation page for Iva Gonzo.',
    canonicalPath: '/support',
  },
  '/submit-your-music-guided-preview': {
    title: 'Guided Preview | Iva Gonzo',
    description: 'Internal guided preview for Iva Gonzo submit flow.',
    canonicalPath: '/submit-your-music',
  },
  [SUBMITHUB_DIRECT_PREVIEW_PATH]: {
    title: 'Submit Your Music | Iva Gonzo (Temporary Preview)',
    description: 'Temporary preview of the Iva Gonzo submission page.',
    canonicalPath: '/submit-your-music',
  },
};
const SOCIAL_PROFILES = [
  'https://open.spotify.com/user/11185646448',
  'https://youtube.com/@ivagonzo/',
  'https://www.pinterest.com/ivagonzo/',
  SUBMITHUB_PROFILE_URL,
];

function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
}

const SUBMIT_FAQS = [
  {
    question: 'Can I submit music to Iva Gonzo Spotify playlists?',
    answer:
      'Yes. Submit Your Music lets artists send a track to independent Spotify playlist curator Iva Gonzo for review and possible playlist consideration when the track fits.',
  },
  {
    question: 'Is this paid playlist placement?',
    answer:
      'No. Payment covers music review, written feedback, and possible playlist consideration. Playlist placement, streams, saves, followers, and streaming results are not guaranteed.',
  },
  {
    question: 'What genres are best for Iva Gonzo playlist submission?',
    answer:
      'The strongest fit is usually dark alternative and underground music, including dark trap, phonk, trap metal, scream rap, hyperpop, glitchcore, shoegaze, post-punk, metalcore, electronic, dnb, reggaeton, afrobeat, and related genre-bending sounds.',
  },
  {
    question: 'Can I submit unreleased music?',
    answer:
      'Yes. Planned-release submissions can use a playable private link and/or uploaded audio file with the planned release date.',
  },
  {
    question: 'Can I submit a track just for review if the release date is not planned yet?',
    answer:
      'Yes. Review only is available with Quick Check, Standard, and Premium. It includes written feedback only, and the track is not evaluated for playlist fit. To request playlist consideration later, use an available credit for a new Planned submission once the release date is confirmed, or a new Released submission once the track is live.',
  },
  {
    question: 'How long does review take?',
    answer:
      'Premium review timing is up to 48 hours. Standard Submission and Quick Check review timing is up to 5 days.',
  },
];
const STRUCTURED_PLAYLISTS = [
  {
    id: 'dark-side',
    name: 'DARK SIDE',
    url: 'https://open.spotify.com/playlist/44chMC8qDiVyiwjzmZio0Y',
    description: 'Dark trap, phonk, trap metal, and scream rap playlist for heavy dark-energy tracks.',
    genre: ['Dark trap', 'Phonk', 'Trap metal', 'Scream rap'],
  },
  {
    id: 'fresh-blood',
    name: 'FRESH BLOOD',
    url: 'https://open.spotify.com/playlist/25Ry369evgzmi7yAimZ3IY',
    description: 'Boundary-pushing underground playlist for cloud rap, vapor trap, glitchcore, sigilkore, and post-punk.',
    genre: ['Cloud rap', 'Vapor trap', 'Glitchcore', 'Sigilkore', 'Post-punk'],
  },
  {
    id: 'diamonds-bullets',
    name: 'DIAMONDS & BULLETS',
    url: 'https://open.spotify.com/playlist/17PGwbqjUBbJnAoceiOqrO',
    description: 'High-energy playlist for drill, jersey club, trap, and hip-hop tracks that hit hard.',
    genre: ['Drill', 'Jersey club', 'Trap', 'Hip-hop'],
  },
  {
    id: 'private-paradise',
    name: 'PRIVATE PARADISE',
    url: 'https://open.spotify.com/playlist/1ZuXbfzuinPKrse2Si8vaK',
    description: 'Emotionally deep chill playlist for ambient, lo-fi, dreampunk, vaporwave, witch house, chill phonk, cloud rap, and vapor trap.',
    genre: ['Ambient', 'Lo-fi', 'Dreampunk', 'Vaporwave', 'Witch house', 'Chill phonk', 'Cloud rap', 'Vapor trap'],
  },
  {
    id: 'infinite-dreams',
    name: 'INFINITE DREAMS',
    url: 'https://open.spotify.com/playlist/32py1335U0YhsYb08xpRmi',
    description: 'Cinematic nostalgia playlist for dramatic post-punk, shoegaze, and dream pop.',
    genre: ['Post-punk', 'Shoegaze', 'Dream pop'],
  },
  {
    id: 'uncontrollable',
    name: 'UNCONTROLLABLE',
    url: 'https://open.spotify.com/playlist/2uLDhw8LY6vmKatUDC0QO6',
    description: 'Mosh-mode playlist for metalcore, nu-metal, post-hardcore, and punk.',
    genre: ['Metalcore', 'Nu-metal', 'Post-hardcore', 'Punk'],
  },
  {
    id: 'beach-hype',
    name: 'BEACH HYPE',
    url: 'https://open.spotify.com/playlist/6nIJ0AhZB3pmWfBLNDOB6O',
    description: 'Sunset-charge playlist for reggaeton, jersey club, afrobeat, and Brazilian funk.',
    genre: ['Reggaeton', 'Jersey club', 'Afrobeat', 'Brazilian funk'],
  },
  {
    id: 'dance-or-die',
    name: 'DANCE OR DIE',
    url: 'https://open.spotify.com/playlist/4UrExGSO60rP6QtOjapAvs',
    description: 'Nightlife heat playlist for bass house, speed house, and electro.',
    genre: ['Bass house', 'Speed house', 'Electro'],
  },
  {
    id: 'nitro-go',
    name: 'NITRO GO!',
    url: 'https://open.spotify.com/playlist/3U0GRcFDcHZ8SWHUMb27tQ',
    description: 'Boosted BPM playlist for neurofunk, drum and bass, halftime drum and bass, and liquid bass.',
    genre: ['Neurofunk', 'Drum and bass', 'Halftime drum and bass', 'Liquid bass'],
  },
  {
    id: 'trippy-glitch',
    name: 'TRIPPY GLITCH',
    url: 'https://open.spotify.com/playlist/3D5cDZl0HlKwYTh9FK4Kmx',
    description: 'Alien-signal playlist for glitchcore, glitchbreak, sigilkore, and hyperpop.',
    genre: ['Glitchcore', 'Glitchbreak', 'Sigilkore', 'Hyperpop'],
  },
].map((playlist) => ({
  ...playlist,
  followerCount: getPlaylistFollowerDisplayCount(playlist.id),
}));

function normalizePath(pathname) {
  const normalized = String(pathname || '/').replace(/\/+$/, '');
  return normalized || '/';
}

function setMetaTag(selector, attribute, name, content) {
  if (!content) return;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

function setJsonLdScripts(items) {
  document.head.querySelectorAll('script[data-route-json-ld="true"]').forEach((node) => node.remove());
  items.forEach((item, index) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-route-json-ld', 'true');
    script.setAttribute('id', `route-json-ld-${index + 1}`);
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

function baseJsonLd(meta, canonicalUrl) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'Iva Gonzo',
      url: SITE_URL,
      description: PUBLIC_ROUTE_META['/'].description,
      publisher: { '@id': `${SITE_URL}/#person` },
      inLanguage: 'en',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      '@id': `${SITE_URL}/#person`,
      name: 'Iva Gonzo',
      url: SITE_URL,
      sameAs: SOCIAL_PROFILES,
      jobTitle: ['Music curator', 'Dark Trap / Phonk artist', 'Producer', 'Digital art creator'],
      knowsAbout: [
        'Spotify playlist curator',
        'Spotify playlist submission',
        'Dark Trap',
        'Phonk',
        'Underground music',
        'Alternative music',
        'Playlist curation',
        'Music review',
        'Written artist feedback',
      ],
      description: 'Independent Dark Trap / Phonk artist, producer, digital art creator, Spotify playlist curator, and music review service operator.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: meta.title,
      description: meta.description,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#person` },
      inLanguage: 'en',
    },
  ];
}

function routeJsonLd(pathname, meta, canonicalUrl) {
  const items = baseJsonLd(meta, canonicalUrl);

  if (pathname === '/submit-your-music') {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${SITE_URL}/submit-your-music#service`,
      name: 'Iva Gonzo Spotify Playlist Submission, Music Review and Playlist Consideration',
      serviceType: 'Spotify playlist submission, music review, written feedback and playlist consideration',
      provider: { '@id': `${SITE_URL}/#person` },
      areaServed: 'Worldwide',
      url: `${SITE_URL}/submit-your-music`,
      description:
        'Paid independent Spotify playlist curator review service with Quick Check, Standard, and Premium options. Quick Check includes a brief written review; Standard and Premium include more detailed written feedback. Released and Planned submissions also include an independent curator decision, while Review only does not. Approved tracks may move to playlist consideration, but placement and streams are not guaranteed.',
      keywords:
        'spotify playlist submission, playlist curator, spotify playlist curator, submit music to playlists, music review, playlist consideration, written feedback, dark trap playlist submission, phonk playlist submission',
      audience: {
        '@type': 'Audience',
        audienceType: 'Independent artists, producers, labels, and music marketers seeking curator review and possible playlist consideration',
      },
      offers: {
        '@type': 'OfferCatalog',
        name: 'Submission plans',
        itemListElement: [
          {
            '@type': 'Offer',
            name: 'Quick Check',
            description: 'Brief written music review with timing up to 5 days. Released and Planned submissions also include an independent curator decision; Review only does not.',
            price: 5,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: '1 Standard Review',
            description: 'One Standard music review with written feedback and playlist consideration when the track fits.',
            price: 13,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: '5 Standard Reviews',
            description: 'Five Standard music reviews with written feedback and playlist consideration when tracks fit.',
            price: 60,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: '10 Standard Reviews',
            description: 'Ten Standard music reviews with written feedback and playlist consideration when tracks fit.',
            price: 110,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: '1 Premium Review',
            description: 'One Premium music review with the most detailed written feedback and fastest review timing.',
            price: 27,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: '5 Premium Reviews',
            description: 'Five Premium music reviews with the most detailed written feedback and fastest review timing.',
            price: 125,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: '10 Premium Reviews',
            description: 'Ten Premium music reviews with the most detailed written feedback and fastest review timing.',
            price: 230,
            priceCurrency: 'USD',
            url: `${SITE_URL}/submit-your-music`,
            availability: 'https://schema.org/InStock',
          },
        ],
      },
      termsOfService: `${SITE_URL}/submit-your-music`,
    });
    items.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/submit-your-music#faq`,
      mainEntity: SUBMIT_FAQS.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  if (pathname === '/playlists') {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/playlists#collection`,
      url: `${SITE_URL}/playlists`,
      name: 'Iva Gonzo Playlists',
      description: meta.description,
      keywords:
        'spotify playlists, spotify playlist curator, dark trap playlist, phonk playlist, trap metal playlist, underground spotify playlists, playlist submission',
      mainEntity: {
        '@type': 'ItemList',
        name: 'Iva Gonzo curator playlists',
        itemListElement: STRUCTURED_PLAYLISTS.map((playlist, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'MusicPlaylist',
            name: playlist.name,
            url: playlist.url,
            description: playlist.description,
            genre: playlist.genre,
            interactionStatistic: {
              '@type': 'InteractionCounter',
              interactionType: { '@type': 'FollowAction' },
              userInteractionCount: playlist.followerCount,
            },
            creator: { '@id': `${SITE_URL}/#person` },
          },
        })),
      },
    });
  }

  if (pathname === '/support') {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/support#support-page`,
      url: `${SITE_URL}/support`,
      name: 'Support Iva Gonzo',
      description: meta.description,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      potentialAction: {
        '@type': 'DonateAction',
        recipient: { '@id': `${SITE_URL}/#person` },
        target: `${SITE_URL}/support`,
      },
    });
  }

  return items;
}

function RouteMetadata() {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    const isNoindex = Object.prototype.hasOwnProperty.call(NOINDEX_ROUTE_META, pathname);
    const meta = PUBLIC_ROUTE_META[pathname] || NOINDEX_ROUTE_META[pathname] || PUBLIC_ROUTE_META['/'];
    const canonicalUrl = `${SITE_URL}${meta.canonicalPath}`;

    document.title = meta.title;
    setCanonical(canonicalUrl);
    setMetaTag('meta[name="description"]', 'name', 'description', meta.description);
    setMetaTag('meta[name="robots"]', 'name', 'robots', isNoindex ? 'noindex,nofollow,noarchive' : 'index,follow,max-image-preview:large');
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', meta.title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', meta.description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', DEFAULT_SOCIAL_IMAGE);
    setMetaTag('meta[property="og:image:alt"]', 'property', 'og:image:alt', 'Iva Gonzo logo and visual identity');
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', DEFAULT_SOCIAL_IMAGE);
    setJsonLdScripts(isNoindex ? [] : routeJsonLd(pathname, meta, canonicalUrl));
  }, [location.pathname]);

  return null;
}

function MetaPixelLoader() {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    if (pathname === '/' || pathname === '/playlists' || pathname === '/support') {
      initMetaPixel();
    }
  }, [location.pathname]);

  return null;
}

function SiteAnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

function App() {
  const { hideInstagram } = useRegionalVisibility();
  const [showIcons, setShowIcons] = useState(false);
  const [currentVisualIndex, setCurrentVisualIndex] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      return 0;
    }

    return getInitialLogoVisualIndexForPage('/');
  });
  const [showGif, setShowGif] = useState(false); // Добавляем состояние для GIF
  const osciCanvasRef = useRef(null);
  const osciAnimatorRef = useRef(null);
  const osciSvgListRef = useRef([]);
  const [osciReady, setOsciReady] = useState(false);
  const currentVisual = LOGO_SEQUENCE[currentVisualIndex] ?? LOGO_SEQUENCE[0];

  useEffect(() => {
    const cleanups = [];
    let isMounted = true;

    const startDecode = () => {
      if (!isMounted) {
        return;
      }
      const links = document.querySelectorAll('.home-page .btn-link');
      links.forEach((link) => {
        cleanups.push(decodeTextLocked(link, link.dataset.text, 1000));
        cleanups.push(applyRandomFlicker(link));
      });
    };

    const cancelFontWait = runAfterFontsReady(startDecode);

    if (window.innerWidth >= 768) {
      document.body.classList.add('desktop');
    }

    const socialIcons = document.querySelectorAll('#social-icons a');
    socialIcons.forEach((icon, index) => {
      setTimeout(() => {
        icon.style.opacity = '1';
        icon.style.transform = 'translateY(0)';
      }, index * 600); // 200ms задержка между каждой иконкой
    });

    return () => {
      isMounted = false;
      cancelFontWait();
      cleanups.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('.home-page .btn-link');
    const cleanups = [];

    sections.forEach((section) => {
      const handleClick = () => {
        sections.forEach(s => s.classList.remove('open-section')); // Удалить класс из всех разделов
        section.classList.toggle('open-section'); // Тогглить класс текущего раздела
      };

      section.addEventListener('click', handleClick);
      cleanups.push(() => section.removeEventListener('click', handleClick));
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  const toggleIcons = () => {
    setShowIcons(!showIcons);
    setShowGif(!showIcons); // Управляем состоянием GIF
    document.querySelector('.btn-link[data-text="MUSIC"]').classList.toggle('open-section', !showIcons);

    if (!showIcons) {
      const icons = document.querySelectorAll('#music-icons img');
      icons.forEach((icon) => {
        icon.style.opacity = 0;
        icon.classList.remove('mobile-bright');
        const randomDelay = Math.random() * 0.5;
        setTimeout(() => {
          const blinkInterval = setInterval(() => {
            icon.style.opacity = Math.random() > 0.5 ? 1 : 0;
          }, 50);
          setTimeout(() => {
            clearInterval(blinkInterval);
            if (window.innerWidth <= 1024) {
              icon.classList.add('mobile-bright');
            } else {
              icon.style.opacity = 0.5;
            }
          }, 1000);
        }, randomDelay * 1000);
      });
    }
  };

  const changeVisual = useCallback(() => {
    setCurrentVisualIndex(getNextGlobalLogoVisualIndex());
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadSvgAssets = async () => {
      try {
        const svgUrls = OSCI_SVG_ASSET_URLS;

        if (!svgUrls.length) {
          console.error('No SVG files found in ./svg for oscii');
          return;
        }

        const svgTexts = await Promise.all(svgUrls.map((url) => fetch(url).then((res) => res.text())));

        if (isMounted) {
          osciSvgListRef.current = svgTexts;
          setOsciReady(true);
        }
      } catch (error) {
        console.error('Failed to load SVG assets', error);
      }
    };

    loadSvgAssets();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const visual = LOGO_SEQUENCE[currentVisualIndex];
    if (visual.type !== 'osci') {
      if (osciAnimatorRef.current) {
        osciAnimatorRef.current.destroy();
        osciAnimatorRef.current = null;
      }
      return undefined;
    }
    if (!osciReady || !window.OsciLogoAnimator) {
      return undefined;
    }

    const canvas = osciCanvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const animator = new window.OsciLogoAnimator({
      canvas,
      svgList: osciSvgListRef.current.length ? osciSvgListRef.current : undefined,
      svgIndex: 0,
      cycleOnRelock: true,
      direction: -1,
      beams: 3,
      transparent: true,
      trailFade: 0.1,
      background: 'rgba(0,0,0,0)',
      beamColor: '#ff0000',
      baseOutlineColor: '#0011ff',
      scan: false,
      vig: 0,
      rgbAberration: 20,
      speed: 1.55,
      speedBoost: 0.5,
      intensity: 2.2,
      highlightWidthPx: 60, //90
      maxFps: 60,
      renderScale: 0.99,
      quality: 1.0,
      adaptive: false,
      traceStepPx: 5, // 0.2
      strokePx: 0.5,
      glowPx: 0,
      miterLimit: 14,
      baseOutlineAlpha: 0.10,
      baseOutlineStrokePx: 0.1,    // толщина базовой обводки (НОВОЕ)
      frameJitter: 0,
      stableJitterPx: 0,
      lostJitterPx: 0,
      dropout: 0.3,
      signalEnabled: true,
      signalNoise: 1,
      signalHoldMin: 3.2,
      signalHoldMax: 4.4,
      signalDropMin: 0.77,
      signalDropMax: 2.28,
      glitch: true,
      glitchChance: 0.37,
    });

    osciAnimatorRef.current = animator;
    animator.start();

    return () => {
      animator.destroy();
      osciAnimatorRef.current = null;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [currentVisualIndex, osciReady]);

  return (
    <Router>
      <RouteMetadata />
      <MetaPixelLoader />
      <SiteAnalyticsRouteTracker />
      <Routes>
        <Route path="/" element={
          <div className="App home-page">
            <div id="background">
              <ResponsiveDecodedBackground
                mobileSrc={require('./bg_noise/mobile_noise.gif')}
                tabletSrc={require('./bg_noise/tablet_noise.gif')}
                desktopSrc={require('./bg_noise/desktop_noise.gif')}
              />
              {showGif && <img src={myWayMyStyleGif} alt="" aria-hidden="true" id="animated-gif" />} {/* Отображаем GIF */}
            </div>
            <div id="content-wrapper">
              <div id="links" className="home-links">
                <button className="btn btn-link flicker" data-text="MUSIC" onClick={toggleIcons}>MUSIC</button>
                <div id="music-icons" className={showIcons ? 'show-icons' : ''}>
                  <a href="https://open.spotify.com/artist/5UDgNySjOfSloHScugFcUB?si=GC8VSoZlTYaDuXFHGR5AeA" target="_blank" rel="noopener noreferrer">
                    <img src={require('./streaming_icons/Spotify.svg').default} alt="Spotify" />
                  </a>
                  <a href="https://music.apple.com/us/artist/iva-gonzo/953559504" target="_blank" rel="noopener noreferrer">
                    <img src={require('./streaming_icons/Apple.svg').default} alt="Apple Music" />
                  </a>
                  <a href="https://music.amazon.com/artists/B00RADPKHS/iva-gonzo?marketplaceId=ATVPDKIKX0DER&musicTerritory=US&ref=dm_sh_HJktL6CvBIkHgiqDrq73Gl9bn" target="_blank" rel="noopener noreferrer">
                    <img src={require('./streaming_icons/Amazon.svg').default} alt="Amazon Music" />
                  </a>
                  <a href="https://music.youtube.com/channel/UCTE04oEXCmud6MTaIT3QBbA?si=QBBAWswlpJlIsIMd" target="_blank" rel="noopener noreferrer">
                    <img src={require('./streaming_icons/Youtube.svg').default} alt="YouTube Music" />
                  </a>
                  <a href="https://deezer.page.link/wEBgitSNZDHxaH4s8" target="_blank" rel="noopener noreferrer">
                    <img src={require('./streaming_icons/Deezer.svg').default} alt="Deezer" />
                  </a>
                  <a href="https://www.pandora.com/artist/iva-gonzo/ARckv9kkqljkkfZ" target="_blank" rel="noopener noreferrer">
                    <img src={require('./streaming_icons/Pandora.svg').default} alt="Pandora" />
                  </a>
                </div>
                <a className="btn btn-link flicker" data-text="PLAYLISTS" href="/playlists">PLAYLISTS</a>
                <a
                  href={PUBLIC_SUBMISSION_URL}
                  target={PUBLIC_SUBMISSION_TARGET}
                  rel={PUBLIC_SUBMISSION_REL}
                  className="btn btn-link flicker"
                  data-text="SUBMIT YOUR MUSIC"
                  onClick={(event) =>
                    handleSubmitMusicIntentClick(event, {
                      source_page: 'home',
                      cta_location: 'main_navigation',
                      destination: PUBLIC_SUBMISSION_URL,
                    })
                  }
                >
                  SUBMIT YOUR MUSIC
                </a>
                {!SUBMITHUB_DIRECT_TEMPORARY_MODE && (
                  <a href="/support" className="btn btn-link flicker" data-text="SUPPORT">SUPPORT</a>
                )}
              </div>
              {currentVisual.type === 'image' ? (
                <DecodedArtImage
                  id="background-gif"
                  src={currentVisual.src}
                  alt="Iva Gonzo logo visual"
                  aspectRatio={currentVisual.aspectRatio}
                  onClick={changeVisual}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      changeVisual();
                    }
                  }}
                />
              ) : (
                <div
                  id="background-gif"
                  className="osci-wrap"
                  onClick={changeVisual}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      changeVisual();
                    }
                  }}
                >
                  {osciReady ? (
                    <canvas id="osci" ref={osciCanvasRef} />
                  ) : null}
                </div>
              )}
            </div>
            <footer id="social-icons">
              {!hideInstagram && (
                <a href="https://www.instagram.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
                  <img className="social-icon" src={require('./soc icons/1_icons8-instagram.svg').default} alt="Instagram" />
                </a>
              )}
              <a href="https://open.spotify.com/user/11185646448?si=e1a94395b1054a48" target="_blank" rel="noopener noreferrer">
                <img className="social-icon" src={require('./soc icons/2_icons8-spotify.svg').default} alt="Spotify" />
              </a>
              <a href="https://youtube.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
                <img className="social-icon" src={require('./soc icons/3_icons8-youtube.svg').default} alt="YouTube" />
              </a>
              {/*<a href="https://www.tiktok.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
                <img className="social-icon" src={require('./soc icons/4_icons8-tiktok.svg').default} alt="TikTok" />
              </a>*/}
              <a href="https://www.pinterest.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
                <img className="social-icon" src={require('./soc icons/5_icons8-pinterest.svg').default} alt="Pinterest" />
              </a>
              {/*<a href="https://x.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
                <img className="social-icon" src={require('./soc icons/6_icons8-twitterx.svg').default} alt="TwitterX" />
              </a>*/}
            </footer>
          </div>
        } />
        <Route
          path="/submit-your-music"
          element={
            SUBMITHUB_DIRECT_TEMPORARY_MODE
              ? <ExternalRedirect to={SUBMITHUB_DIRECT_URL} />
              : <SubmitYourMusicPage />
          }
        />
        {SUBMITHUB_DIRECT_TEMPORARY_MODE && (
          <Route path={SUBMITHUB_DIRECT_PREVIEW_PATH} element={<SubmitYourMusicPage />} />
        )}
        <Route path="/submit-your-music-guided-preview" element={<SubmitYourMusicGuidedPreviewPage />} />
        <Route path="/checkout" element={<Navigate to="/submit-your-music" replace />} />
        <Route path="/thank-you" element={<ThankYouPage />} /> {/* Новый маршрут */}
        <Route path="/playlists" element={<PlaylistsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/thank-you" element={<SupportThankYouPage />} />
      </Routes>
    </Router>
  );
}

export default App;
